<template>
  <div class="linkInfoPage divContainer genericResourceModule">
    <div v-if="loading" v-loading="loading" class="fullContainer" />

    <div v-else-if="loadError" class="linkInfoPage__error">
      <p class="linkInfoPage__errorTitle">资源加载失败</p>
      <p class="linkInfoPage__errorDesc link-text-muted">{{ loadError }}</p>
      <el-button size="small" type="primary" @click="goToList">返回列表</el-button>
    </div>

    <div v-else class="linkInfoPage__layout resourceFormPage__layout">
      <div class="resourceFormPage__headerDock">
        <header class="resourceFormPage__actionBar">
          <div class="resourceFormPage__lead">
            <h1 class="resourceFormPage__pageTitle">查看资源</h1>
            <span class="resourceFormPage__typeReadonly link-font-mono">{{ info.assetType || 'ai_assistant' }}</span>
          </div>
          <div class="resourceFormPage__actions">
            <button
              v-if="info.onOrOff === 0"
              type="button"
              class="resourceFormPage__btn resourceFormPage__btn--ghost"
              @click="toggleOnlineStatus"
            >
              上线
            </button>
            <button
              v-else-if="info.onOrOff === 1"
              type="button"
              class="resourceFormPage__btn resourceFormPage__btn--ghost"
              @click="toggleOnlineStatus"
            >
              下线
            </button>
            <button type="button" class="resourceFormPage__btn resourceFormPage__btn--ghost" @click="deleteResource">
              删除
            </button>
            <button type="button" class="resourceFormPage__btn resourceFormPage__btn--primary" @click="goToEdit">
              编辑
            </button>
          </div>
        </header>
      </div>

      <main
        class="linkInfoPage__main resourceFormPage__main main"
        :class="{ 'linkInfoPage__main--jsonOpen': jsonSheetOpen }"
      >
        <generic-resource-json-top-sheet
          :visible.sync="jsonSheetOpen"
          :payload="jsonSheetPayload"
        >
          <el-card class="card linkInfoSection">
            <div slot="header">
              <strong class="linkInfoSection__title">基本信息</strong>
            </div>
            <div class="linkInfoBasic">
              <div class="linkInfoBasic__preview">
                <el-image
                  v-if="info.thumbUrl"
                  class="linkInfoPreview__img"
                  :src="info.thumbUrl"
                  :preview-src-list="[info.thumbUrl]"
                  fit="cover"
                >
                  <div slot="error" class="linkInfoPreview__empty">
                    <i class="el-icon-picture-outline" aria-hidden="true" />
                    <span>加载失败</span>
                  </div>
                </el-image>
                <div v-else class="linkInfoPreview__empty">
                  <i class="el-icon-user" aria-hidden="true" />
                  <span>暂无头像</span>
                </div>
              </div>

              <div class="linkInfoBasic__body">
                <h1 class="linkInfoBasic__name">{{ displayName }}</h1>
                <p class="linkInfoBasic__subtitle">角色名：{{ roleName }}</p>

                <div class="linkInfoBasic__keyRow">
                  <span class="linkInfoBasic__keyLabel">Key</span>
                  <template v-if="info.key">
                    <code class="linkInfoBasic__keyValue link-font-mono" :title="info.key">
                      {{ displayKey }}
                    </code>
                    <button type="button" class="linkInfoBasic__copyBtn" @click="copyKey">复制</button>
                  </template>
                  <span v-else class="linkInfoSection__empty">—</span>
                </div>

                <div class="linkInfoBasic__grid">
                  <div class="linkInfoBasic__cell">
                    <div class="linkInfoBasic__label">类型</div>
                    <span class="linkInfoPill linkInfoPill--type linkInfoPill--type-violet">
                      ai_assistant
                    </span>
                  </div>
                  <div class="linkInfoBasic__cell">
                    <div class="linkInfoBasic__label">状态</div>
                    <span
                      class="statusPill"
                      :class="'statusPill--' + getStatusPillVariant(info.onOrOff)"
                    >
                      <span class="statusPill__dot" aria-hidden="true" />
                      {{ getStatusLabel(info.onOrOff) }}
                    </span>
                  </div>
                  <div class="linkInfoBasic__cell">
                    <div class="linkInfoBasic__label">分类</div>
                    <div
                      v-if="info.categoryList && info.categoryList.length"
                      class="linkInfoBasic__tagRow"
                    >
                      <router-link
                        v-for="item in info.categoryList"
                        :key="item.id"
                        :to="{ path: '/collection/classify/info/' + item.id }"
                        class="linkInfoBasic__categoryLink"
                      >
                        <span class="linkInfoPill linkInfoPill--category">{{ item.name }}</span>
                      </router-link>
                    </div>
                    <span v-else class="linkInfoSection__empty">未关联</span>
                  </div>
                  <div class="linkInfoBasic__cell">
                    <div class="linkInfoBasic__labelRow">
                      <span class="linkInfoBasic__label linkInfoBasic__label--inline">配置数据集</span>
                      <span class="linkInfoBasic__hint">
                        只统计下面「关联解锁」「过滤条件」里填过的数据集；列表按数据集筛选看的是分类是否在该数据集里，两边可能对不上。
                      </span>
                    </div>
                    <div v-if="datasetNames.length" class="linkInfoBasic__tagRow">
                      <span
                        v-for="name in datasetNames"
                        :key="name"
                        class="linkInfoPill linkInfoPill--dataset"
                      >
                        {{ name }}
                      </span>
                    </div>
                    <span v-else class="linkInfoSection__empty">未配置</span>
                  </div>
                </div>
              </div>
            </div>
          </el-card>

          <generic-resource-ai-assistant-readonly :info="info" />

          <generic-resource-unlock-readonly :info="info" />

          <el-card class="card linkInfoSection">
            <div slot="header" class="linkInfoSection__head">
              <strong class="linkInfoSection__title">关联 Tag</strong>
              <span v-if="tagCount" class="linkInfoSection__badge">{{ tagCount }}</span>
            </div>
            <div v-if="tagCount" class="linkInfoTagList">
              <span
                v-for="tag in info.tagVOList"
                :key="tag.key || tag.id"
                class="linkInfoPill linkInfoPill--tag"
              >
                {{ tag.name }}
              </span>
            </div>
            <span v-else class="linkInfoSection__empty">未关联 Tag</span>
          </el-card>

          <generic-resource-filter-readonly :info="info" />

          <el-card class="card linkInfoSection">
            <div slot="header">
              <strong class="linkInfoSection__title">其他</strong>
            </div>
            <div class="linkInfoRemark">
              <div class="linkInfoRemark__label">备注</div>
              <p v-if="info.comment" class="linkInfoRemark__text">{{ info.comment }}</p>
              <span v-else class="linkInfoSection__empty">无备注</span>
            </div>
          </el-card>
        </generic-resource-json-top-sheet>
      </main>
    </div>
  </div>
</template>

<script>
import {
  getAiAssistantById,
  deleteAiAssistantById,
  onOrOff,
} from '../../../api/aiAssistant'
import { getAllDataList } from '../../../api/classifyManage'
import GenericResourceFilterReadonly from '../genericResourceManage/GenericResourceFilterReadonly.vue'
import GenericResourceJsonTopSheet from '../genericResourceManage/GenericResourceJsonTopSheet.vue'
import GenericResourceUnlockReadonly from '../genericResourceManage/GenericResourceUnlockReadonly.vue'
import { getStatusLabel, getStatusPillVariant } from '../genericResourceManage/batchTag/resourceDisplayHelper'
import GenericResourceAiAssistantReadonly from '../genericResourceManage/GenericResourceAiAssistantReadonly.vue'
import {
  aiAssistantDisplayTitle,
  aiAssistantRoleName,
  buildAiAssistantJsonSheetPayload,
  collectResourceDatasetNames,
  normalizeAiAssistantInfoDetail,
} from './aiAssistantInfoHelper'
import { resolveLayoutBackTo, buildLocationWithBackTo } from '../../../utils/layoutNavigation'

export default {
  name: 'AiAssistantInfo',
  components: {
    GenericResourceAiAssistantReadonly,
    GenericResourceFilterReadonly,
    GenericResourceJsonTopSheet,
    GenericResourceUnlockReadonly,
  },
  data() {
    return {
      info: {
        jsonConfig: {},
        categoryList: [],
        tagVOList: [],
        wallpaperLockGroupVOList: [],
        wallpaperAppVersionList: [],
        countryList: {},
        comment: '',
      },
      loading: false,
      loadError: '',
      jsonSheetOpen: false,
      dataOptions: [],
    }
  },
  computed: {
    displayName() {
      return aiAssistantDisplayTitle(this.info)
    },
    roleName() {
      return aiAssistantRoleName(this.info)
    },
    displayKey() {
      const key = this.info.key
      if (!key) return '—'
      if (key.length <= 36) return key
      return `${key.slice(0, 16)}…${key.slice(-12)}`
    },
    jsonSheetPayload() {
      return buildAiAssistantJsonSheetPayload(this.info)
    },
    datasetNames() {
      return collectResourceDatasetNames(
        this.info.wallpaperLockGroupVOList,
        this.info.wallpaperAppVersionList
      )
    },
    tagCount() {
      return (this.info.tagVOList || []).length
    },
  },
  created() {
    this.getAllData()
    this.loadDetail()
  },
  methods: {
    getStatusLabel,
    getStatusPillVariant,
    getAllData() {
      getAllDataList().then((res) => {
        if (res.success) {
          this.dataOptions = res.data
        }
      })
    },
    goToList() {
      const backTo = resolveLayoutBackTo(this.$route, '/resource/aiAssistant/list')
      this.$router.push(backTo)
    },
    goToEdit() {
      const id = this.info.id || this.$route.params.id
      if (!id) {
        this.$message.warning('资源 ID 缺失，无法进入编辑页')
        return
      }
      this.$router.push(
        buildLocationWithBackTo(
          `/resource/aiAssistant/edit/${id}`,
          this.$route.query && this.$route.query.backTo
        )
      )
    },
    deleteResource() {
      const id = this.info.id || this.$route.params.id
      if (!id) {
        this.$message.warning('资源 ID 缺失，无法删除')
        return
      }
      this.$confirm('此操作将删除该角色, 是否继续?', '提示', { type: 'warning' })
        .then(() => deleteAiAssistantById({ id }))
        .then((res) => {
          if (res.success) {
            this.$message.success('删除成功')
            this.goToList()
          }
        })
        .catch(() => {})
    },
    toggleOnlineStatus() {
      const id = this.info.id || this.$route.params.id
      if (!id) {
        return
      }
      const nextStatus = this.info.onOrOff === 1 ? 0 : 1
      onOrOff({ id, onOrOff: nextStatus }).then((res) => {
        if (res.success) {
          this.$message.success(nextStatus === 1 ? '已上线' : '已下线')
          this.loadDetail()
        }
      })
    },
    copyKey() {
      const key = this.info.key
      if (!key) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(key).then(() => {
          this.$message.success('Key 已复制')
        })
        return
      }
      this.$message.warning('当前环境不支持自动复制')
    },
    loadDetail() {
      this.loading = true
      this.loadError = ''
      getAiAssistantById({ id: this.$route.params.id })
        .then((res) => {
          if (!res.success || !res.data) {
            this.loadError = '未获取到资源详情，请返回列表重试'
            return
          }
          this.info = normalizeAiAssistantInfoDetail(res.data)
        })
        .catch(() => {
          this.loadError = '网络异常，请稍后重试'
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>

<style scoped>
@import '../genericResourceManage/resourceFormPage.css';
@import '../genericResourceManage/linkListTheme.css';
@import '../genericResourceManage/linkModuleSurfaces.css';
@import '../genericResourceManage/linkTypography.css';
@import '../genericResourceManage/linkSemanticPills.css';

.divContainer,
.linkInfoPage__layout {
  height: 100%;
}

.fullContainer {
  width: 100%;
  height: 100%;
}

.linkInfoPage__layout {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background: var(--link-canvas);
}

.linkInfoPage__main {
  flex: 1;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
  padding: 20px;
  overflow: auto;
}

.linkInfoPage__main--jsonOpen {
  overflow: hidden;
}

.linkInfoSection {
  margin-bottom: 16px;
}

.linkInfoSection__head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.linkInfoSection__title {
  font-size: var(--link-text-lg);
  font-weight: var(--link-font-semibold);
}

.linkInfoSection__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: var(--link-text-xs);
  font-weight: var(--link-font-medium);
  color: var(--link-ink-secondary);
  background: var(--link-surface-muted, #f0ebe3);
}

.linkInfoSection__empty {
  font-size: var(--link-text-md);
  color: var(--link-ink-muted);
}

.linkInfoBasic {
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

.linkInfoBasic__body {
  min-width: 0;
}

.linkInfoBasic__name {
  margin: 0 0 4px;
  font-size: var(--link-text-xl);
  font-weight: var(--link-font-semibold);
}

.linkInfoBasic__subtitle {
  margin: 0 0 12px;
  font-size: var(--link-text-sm);
  color: var(--link-ink-secondary);
}

.linkInfoBasic__keyRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--link-line, #eee9e2);
}

.linkInfoBasic__keyLabel,
.linkInfoBasic__label,
.linkInfoRemark__label {
  font-size: var(--link-text-sm);
  font-weight: var(--link-font-medium);
  color: var(--link-ink-secondary);
}

.linkInfoBasic__keyValue {
  font-size: var(--link-text-sm);
  color: var(--link-ink-muted);
  background: var(--link-surface-muted, #f0ebe3);
  padding: 2px 8px;
  border-radius: 6px;
}

.linkInfoBasic__copyBtn {
  padding: 0 6px;
  border: none;
  background: none;
  font-size: var(--link-text-xs);
  color: var(--link-accent);
  cursor: pointer;
}

.linkInfoBasic__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 32px;
}

.linkInfoBasic__cell {
  min-width: 0;
}

.linkInfoBasic__labelRow {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 8px;
  margin-bottom: 6px;
}

.linkInfoBasic__label--inline {
  margin-bottom: 0;
  flex-shrink: 0;
}

.linkInfoBasic__hint {
  margin: 0;
  flex: 1;
  min-width: 12rem;
  font-size: var(--link-text-xs);
  font-weight: var(--link-font-regular);
  color: var(--link-ink-muted);
  line-height: 1.45;
}

.linkInfoBasic__label {
  margin-bottom: 6px;
}

.linkInfoBasic__tagRow,
.linkInfoTagList {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.linkInfoBasic__categoryLink {
  text-decoration: none;
}

.linkInfoPill {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: var(--link-text-xs);
  font-weight: var(--link-font-medium);
}

.linkInfoPill--type-violet {
  background: rgba(167, 139, 250, 0.25);
  color: #6d28d9;
}

.linkInfoPill--category,
.linkInfoPill--dataset,
.linkInfoPill--tag {
  background: var(--link-surface-muted, #f7f4ef);
  border: 1px solid var(--link-line, #eee9e2);
  color: var(--link-ink-secondary, #57534e);
}

.linkInfoPreview__img,
.linkInfoPreview__empty {
  width: 200px;
  height: 200px;
  border-radius: 12px;
  border: 1px solid var(--link-line, #eee9e2);
}

.linkInfoPreview__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--link-surface-muted, #f0ebe3);
  color: var(--link-ink-muted);
}

.linkInfoPage .statusPill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px 3px 6px;
  border-radius: 999px;
  font-size: var(--link-text-xs);
  font-weight: var(--link-font-medium);
}

.linkInfoPage .statusPill__dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
}

.linkInfoPage .statusPill--online {
  background: rgba(167, 243, 208, 0.4);
  color: var(--link-status-success-ink);
}

.linkInfoPage .statusPill--offline {
  background: var(--link-workspace, #f3f0ea);
  color: var(--link-ink-muted);
}

.linkInfoPage .statusPill--pending {
  background: var(--link-status-warning-bg);
  color: var(--link-status-warning-ink);
}

.linkInfoPage .statusPill--failed {
  background: var(--link-status-error-bg);
  color: var(--link-status-error-ink);
}

.linkInfoRemark__text {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.linkInfoPage__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 100%;
  padding: 24px;
}

@media (max-width: 720px) {
  .linkInfoBasic,
  .linkInfoBasic__grid {
    grid-template-columns: 1fr;
  }
}
</style>
